// All content sourced from Krystal Technology Business Information document.
// Facts (clients, projects, specs, units) are verbatim. Positioning copy reframed
// as precision engineering & manufacturing (no "fabricator" / small-business language).

export const COMPANY = {
  name: "Krystal Technology",
  tagline: "Where Engineering Meets Excellence",
  location: "Ambernath, Maharashtra, India",
  experienceYears: "30+",
  positioning:
    "A precision engineering and manufacturing partner for critical, high-tolerance applications across defence, marine & offshore, and heavy engineering.",
  overview:
    "Krystal Technology delivers high-precision, critical components and assemblies with strict adherence to quality standards, inspection requirements, and third-party approvals (TPA). With an in-depth command of a wide range of raw materials and their applications, we engineer superior, tailored solutions for the most demanding industrial programmes.",
};

export const CONTACT = {
  contactName: "Tanmay Panchal",
  phone: "+91 98339 74644",
  email: "admin@krystaltechno.com",
  linkedin: "https://www.linkedin.com/in/tanmayhpanchal/",
  brochure: "/assets/brochure/Krystal-Technology-Brochure.pdf",
  office: {
    label: "Registered Office",
    line: "Plot No. A-115, Anandnagar, Additional MIDC, Ambernath - 421506, Maharashtra, India",
  },
  addresses: [
    {
      unit: "Unit I — Machine Shop",
      line: "Wagle Estate, Thane, Maharashtra, India",
    },
    {
      unit: "Unit II — Machine Shop",
      line: "MIDC, Ambernath, Maharashtra, India",
    },
    {
      unit: "Unit III — Fabrication Shop",
      line: "MIDC, Ambernath, Maharashtra, India",
    },
  ],
};

export const INDUSTRIES = [
  {
    id: "steel",
    name: "Steel Industry",
    image: "/assets/images/industries/steel.jpg",
    blurb:
      "Precision assemblies and machined components for continuous casting, rolling and metals-processing lines.",
  },
  {
    id: "marine",
    name: "Marine & Offshore",
    image: "/assets/images/industries/marine.jpg",
    blurb:
      "High-tolerance structures and cartridges for naval platforms and offshore equipment programmes.",
  },
  {
    id: "defence",
    name: "Defence",
    image: "/assets/images/industries/defence.jpg",
    blurb:
      "Mission-critical submarine, naval and land-systems components built to inspection and TPA standards.",
  },
];

export const WHY_CHOOSE = [
  {
    no: "01",
    title: "Critical Precision",
    text: "Every component is engineered for high-tolerance, mission-critical service — spanning submarine assemblies, dredging components, and specialized missile launcher structures.",
  },
  {
    no: "02",
    title: "Three Decades of Legacy",
    text: "Over 30 years serving heavy engineering industries, with a proven record on India's most demanding defence and steel programmes.",
  },
  {
    no: "03",
    title: "Certifications & Standards Compliance",
    text: "An ISO 9001:2015-certified quality system with welding to the ISO 15609-1 standard by BV Marine-qualified welders, strict adherence to third-party approvals (TPA), and backed by an in-house inspection facility.",
  },
  {
    no: "04",
    title: "Advanced Material Expertise",
    text: "Deep technical proficiency in specialized alloys and heavy-duty materials, delivering tailored solutions for high-stress environments.",
  },
  {
    no: "05",
    title: "Zero-Defect, On-Time Delivery",
    text: "A proven track record of zero-defect manufacturing and on-time delivery, consistently meeting the most stringent project timelines for our global clients.",
  },
  {
    no: "06",
    title: "Export-Ready Quality",
    text: "Manufacturing units built to international benchmarks, serving global clients such as Royal IHC and SMS Group with export-ready quality.",
  },
];

export const CAPABILITY_OVERVIEW = [
  {
    id: "equipment",
    no: "01",
    title: "Equipment",
    text: "VMCs, horizontal boring machines and CNC turning centres for large, heavy and high-tolerance work.",
    href: "/capabilities/equipment",
  },
  {
    id: "infrastructure",
    no: "02",
    title: "Infrastructure",
    text: "Three manufacturing units across Thane and Ambernath with lifting capacity up to 15 tons.",
    href: "/capabilities/infrastructure",
  },
];

export const DRIVERS = [
  {
    no: "01",
    title: "Customer Commitment",
    text: "We put our customers' requirements at the centre of everything — delivering to specification, on time, every time.",
  },
  {
    no: "02",
    title: "Continuous Improvement",
    text: "We constantly refine our processes, capability and capacity to raise the standard of what we deliver.",
  },
  {
    no: "03",
    title: "Long-Term Partnership",
    text: "We build enduring relationships with the leaders of defence, marine and heavy engineering — earning trust one delivered programme at a time.",
  },
  {
    no: "04",
    title: "Quality",
    text: "Strict adherence to inspection requirements and third-party approvals, backed by an in-house inspection culture on every part we deliver.",
  },
];

export const EQUIPMENT = {
  summary: [
    { label: "Vertical Machining Centre (VMC)", count: "3 nos." },
    { label: "Horizontal Boring Machine", count: "2 nos." },
    { label: "CNC Turning Centre", count: "1 no." },
    { label: "Radial Drill", count: "1 no." },
  ],
  welding: [
    { label: "Arc Welding", count: "8 nos." },
    { label: "MIG Welding", count: "4 nos." },
    { label: "Argon Welding", count: "2 nos." },
    { label: "Baking Oven", count: "2 nos." },
    { label: "Portable Oven", count: "8 nos." },
  ],
  machines: [
    {
      name: "AWEA",
      type: "Vertical Machining Centre",
      travel: "5200 / 3200 / 1450 mm",
      table: "5020 × 2400 mm",
      capacity: "18 Tons",
      image: "/assets/images/machines/machine_awea_vmc.jpg",
    },
    {
      name: "MAZAK",
      type: "Vertical Machining Centre",
      travel: "1500 / 600 / 600 mm",
      table: "1500 × 500 mm",
      capacity: "1.5 Tons",
      image: "/assets/images/machines/machine_mazak_vmc.jpg",
    },
    {
      name: "KITAMURA",
      type: "Vertical Machining Centre",
      travel: "1200 / 600 / 600 mm",
      table: "1200 × 500 mm",
      capacity: "1.5 Tons",
      image: "/assets/images/machines/machine_kitamura_vmc.jpg",
    },
    {
      name: "BACAU",
      type: "Horizontal Boring (W-1000 mm, Spindle Ø 130 mm)",
      travel: "3000 / 2500 / 1800 mm",
      table: "1800 × 2000 mm",
      capacity: "12 Tons",
      image: "/assets/images/machines/machine_bacau_horizontal_boring.jpg",
    },
    {
      name: "TOS",
      type: "Horizontal Boring (W-900 mm, Spindle Ø 130 mm)",
      travel: "3500 / 2000 / 1800 mm",
      table: "1800 × 1800 mm",
      capacity: "12 Tons",
      image: "/assets/images/machines/machine_tos_horizontal_boring.jpg",
    },
    {
      name: "DAEWOO PUMA 250L",
      type: "CNC Turning Centre",
      travel: "Turning Ø 370 mm",
      table: "Turning length 1300 mm",
      capacity: "—",
      image: "/assets/images/machines/cnc_turning_centre_daewoo_puma.jpg",
    },
  ],
};

export const INFRASTRUCTURE = {
  units: [
    {
      unit: "Unit I",
      type: "Machine Shop",
      location: "Wagle Estate, Thane",
      area: "1,500 sq ft",
      capacity: "5 Tons",
      power: "50 HP",
      image: "/assets/images/workshop/unit1_wagle_estate_machine_shop.jpg",
    },
    {
      unit: "Unit II",
      type: "Machine Shop",
      location: "Ambernath",
      area: "12,000 sq ft",
      capacity: "15 Tons",
      power: "80 HP",
      image: "/assets/images/workshop/unit2_ambernath_machine_shop_interior.jpg",
    },
    {
      unit: "Unit III",
      type: "Fabrication Shop",
      location: "Ambernath",
      area: "12,000 sq ft",
      capacity: "15 Tons",
      power: "65 HP",
      image: "/assets/images/workshop/unit3_ambernath_fabrication_shop_interior.jpg",
    },
  ],
  workforce: [
    { label: "Machine Operators", count: "5" },
    { label: "Fabrication Fitters", count: "4" },
    { label: "Welders (BV Marine Qualified, ISO 15609-1)", count: "4" },
    { label: "Grindermen", count: "6" },
    { label: "Unskilled Labour", count: "6" },
    { label: "Contract Manpower", count: "On demand" },
  ],
  gallery: [
    "/assets/images/workshop/unit2_ambernath_exterior_gate.jpg",
    "/assets/images/workshop/unit3_ambernath_exterior_gate.jpg",
    "/assets/images/workshop/unit1_wagle_estate_machine_shop.jpg",
    "/assets/images/workshop/about_us_warehouse_interior.jpg",
  ],
};

export const TIMELINE = [
  {
    year: "1993",
    title: "The Foundation",
    text: "Established as Weltech Industries Pvt Ltd — the beginning of our precision engineering journey.",
  },
  {
    year: "1996",
    title: "Early Manufacturing Expansion",
    text: "Grew our manufacturing footprint and expanded production capability.",
  },
  {
    year: "2000",
    title: "Fabrication & Machining In-House",
    text: "Transitioned to full-fledged fabrication and machining under one roof.",
  },
  {
    year: "2005",
    title: "Advanced CNC Machinery",
    text: "Introduced advanced CNC machinery to elevate precision and throughput.",
  },
  {
    year: "2008",
    title: "Dredging, Marine & Defence",
    text: "Specialized in dredging, marine and defence projects.",
  },
  {
    year: "2012",
    title: "Multiple Manufacturing Units",
    text: "Scaled up operations with multiple manufacturing units.",
  },
  {
    year: "2020",
    title: "Reliability During Crisis",
    text: "Sustained reliable delivery for our clients amid pandemic disruption.",
  },
];

export const MISSION = {
  mission:
    "To deliver precision fabricated and machined custom-equipment on time, to specification, and to the demanding quality and inspection standards our customers require, across engineering industries.",
  vision:
    "We aspire to be a complete advanced manufacturing partner to the world's most demanding industries, building on our foundation in precision fabrication and machining to deliver the critical components that cannot afford to fail.",
  objectives: [
    "Operate a rigorous zero-defect system across all units, ensuring precision in every project we deliver.",
    "Uphold strict adherence to client specifications and third-party approvals without compromise.",
    "Maintain an in-house inspection culture that guarantees 100% conformance for every component.",
    "Employ BV Marine-qualified workforce and continuously qualify our people to keep getting better.",
    "Maintain a skilled workforce of BV Marine-qualified welders (ISO 15609-1) and modern manufacturing capability.",
  ],
};

// Certifications & standards
export const CERTIFICATIONS = [
  {
    label: "ISO 9001:2015",
    logo: "/assets/brand/iso-9001-2015.png",
    detail: "Uncompromising quality control at every stage, ensuring every component meets global precision standards.",
  },
  {
    label: "ISO 14001:2015",
    logo: "/assets/brand/iso-14001-2015.png",
    detail: "Commitment to sustainable operations and environmentally responsible manufacturing processes.",
  },
  {
    label: "ISO 45001:2018",
    logo: "/assets/brand/iso-45001-2018.png",
    detail: "A rigorous safety culture that protects our workforce and ensures operational continuity.",
  },
  {
    label: "BV Marine Qualified",
    logo: "/assets/brand/bureau-veritas.png",
    detail: "Specialized welding expertise certified by Bureau Veritas, delivering superior structural integrity for extreme maritime environments.",
  },
  {
    label: "ISO 15609-1",
    logo: "/assets/brand/bureau-veritas.png",
    detail: "Qualified welding procedure specifications ensuring repeatable high-tolerance results for specialized alloy fabrication.",
  },
];

// Client name -> logo file (real logos provided)
export const CLIENTS = [
  { name: "Royal IHC", logo: "/assets/logos/royalIHC.png" },
  { name: "SMS Group", logo: "/assets/logos/smsgroup.png" },
  { name: "Mazagon Dock Limited", logo: "/assets/logos/mazgaon.png" },
  { name: "Larsen & Toubro", logo: "/assets/logos/larsentoubro.jpg" },
  { name: "Goa Shipyard Ltd", logo: "/assets/logos/goashipyard.jpg" },
  { name: "SAIL", logo: "/assets/logos/sail.png" },
  { name: "Tata Steel", logo: "/assets/logos/tatasteel.jpg" },
  { name: "Tata Power", logo: "/assets/logos/tatapower.png" },
  { name: "John Cockerill", logo: "/assets/logos/johncockerill.jpg" },
  { name: "Tenova Metals", logo: "/assets/logos/tenova.jpg" },
  { name: "Primetals", logo: "/assets/logos/primetals.png" },
  { name: "Mukand Steel", logo: "/assets/logos/mukand.png" },
];
